"use client"

import { ApertureStyles, T, FM, MAXW, SectionEyebrow } from "./_shared"

export default function ApertureProductShowcase() {
  return (
    <section id="product" className="relative" style={{ background: T.bgSoft }}>
      <ApertureStyles />
      <div className={`${MAXW} mx-auto px-6 lg:px-10 py-12 sm:py-16 md:py-24`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-5">
            <SectionEyebrow>The product</SectionEyebrow>
            <h2
              className="mt-3 leading-[1.0] tracking-[-0.035em]"
              style={{ fontSize: "clamp(2rem, 4vw, 3.1rem)", fontWeight: 800 }}
            >
              For the person who has to{" "}
              <em style={{ color: T.acc, fontStyle: "italic", fontWeight: 700 }}>
                defend the list.
              </em>
            </h2>
            <p className="mt-5" style={{ fontSize: 15.5, color: T.ink2, lineHeight: 1.6 }}>
              Every rejection has a reason. Every reason is sourced. Hand the same audit
              trail to compliance, your CMO, and the auditor — they all get the same
              answer, and none of them have to take your word for it.
            </p>
            <div className="mt-8 flex flex-col gap-3">
              {[
                "Per-row provenance — which check flagged it, on which run.",
                "CSV export with the audit column attached.",
                "Webhooks on completion — wire it to your pipeline.",
              ].map((b) => (
                <div key={b} className="flex items-start gap-3">
                  <span className="mt-[6px] flex-shrink-0" style={{ width: 6, height: 6, borderRadius: 999, background: T.acc }} />
                  <span style={{ fontSize: 14, color: T.ink2 }}>{b}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <DashboardMock />
          </div>
        </div>
      </div>
    </section>
  )
}

function DashboardMock() {
  return (
    <div
      className="relative rounded-2xl overflow-hidden"
      style={{
        background: T.surface,
        border: `1px solid ${T.hair2}`,
        boxShadow: `0 40px 80px -40px rgba(10,10,10,0.25), 0 0 0 1px ${T.hair} inset, 0 0 60px -28px ${T.accGlow}`,
      }}
    >
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-[3px] pointer-events-none z-10"
        style={{
          background: `linear-gradient(90deg, transparent, ${T.acc} 50%, transparent)`,
          filter: "blur(1.5px)",
          opacity: 0.55,
          animation: "apl-scan-y 4.2s linear infinite",
        }}
      />

      <div
        className="flex items-center gap-2 px-4 py-2.5"
        style={{ borderBottom: `1px solid ${T.hair}`, background: T.bgSoft }}
      >
        <span style={{ width: 10, height: 10, borderRadius: 999, background: "rgba(10,10,10,0.10)" }} />
        <span style={{ width: 10, height: 10, borderRadius: 999, background: "rgba(10,10,10,0.10)" }} />
        <span style={{ width: 10, height: 10, borderRadius: 999, background: "rgba(10,10,10,0.10)" }} />
        <span className="ml-3" style={{ fontFamily: FM, fontSize: 11, color: T.ink3, letterSpacing: "0.04em" }}>
          databridge.so / dashboard / dataset · q2-prospects.csv
        </span>
        <span
          className="ml-auto inline-flex items-center gap-1.5 px-2 py-0.5 rounded"
          style={{
            background: T.accChip, color: T.acc, border: `1px solid rgba(15,81,50,0.28)`,
            fontFamily: FM, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
          }}
        >
          <span style={{ width: 5, height: 5, background: T.acc, borderRadius: 999, animation: "apl-pulse 1.6s infinite" }} />
          sample
        </span>
      </div>

      <div className="p-5 sm:p-6 grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-4 flex flex-col gap-3">
          {[
            { l: "Processed",  v: "124,890",  hue: T.blue, d: "+8.4%"  },
            { l: "Cleaned",    v: "18,402",   hue: T.acc,  d: "−12.0%" },
            { l: "Affected",   v: "312",      hue: T.bad,  d: "−4.1%"  },
          ].map((k) => (
            <div
              key={k.l}
              className="rounded-xl p-4"
              style={{ background: T.surface2, border: `1px solid ${T.hair}` }}
            >
              <div className="flex items-center justify-between">
                <span style={{ fontFamily: FM, fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", color: T.ink3 }}>
                  {k.l}
                </span>
                <span
                  className="inline-block rounded-full"
                  style={{ width: 6, height: 6, background: k.hue, boxShadow: `0 0 8px ${k.hue}55`, animation: "apl-blink 4s linear infinite" }}
                />
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="tabular-nums" style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.025em" }}>
                  {k.v}
                </span>
                <span style={{ fontFamily: FM, fontSize: 11, color: k.hue, fontWeight: 600 }}>{k.d}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="col-span-12 lg:col-span-8 flex flex-col gap-4">
          <div className="rounded-xl p-4" style={{ background: T.surface2, border: `1px solid ${T.hair}` }}>
            <div className="flex items-center justify-between">
              <span style={{ fontFamily: FM, fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", color: T.ink3 }}>
                Cleaning activity · 30d
              </span>
              <span style={{ fontFamily: FM, fontSize: 11, color: T.ink3 }}>peak Apr 12</span>
            </div>
            <MockChart />
          </div>

          <div className="rounded-xl" style={{ background: T.surface2, border: `1px solid ${T.hair}` }}>
            {[
              { e: "founder@stripe.com",    s: "Deliverable",  c: T.acc },
              { e: "info@acme.io",          s: "Role-based",   c: T.warn },
              { e: "test@mailinator.com",   s: "Disposable",   c: T.bad },
              { e: "sara@gmial.com",        s: "Likely typo",  c: T.warn },
            ].map((r, i) => (
              <div
                key={r.e}
                className="flex items-center justify-between px-4 py-3"
                style={{ borderTop: i > 0 ? `1px solid ${T.hair}` : undefined }}
              >
                <span style={{ fontFamily: FM, fontSize: 12.5, color: T.ink2 }}>{r.e}</span>
                <span className="inline-flex items-center" style={{ fontSize: 12, color: T.ink2 }}>
                  <span style={{ width: 6, height: 6, borderRadius: 999, background: r.c, marginRight: 8 }} />
                  {r.s}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function MockChart() {
  const W = 560
  const H = 130
  const pts1 = makePts(28, 1, 0.8, 12, H - 28, 18)
  const pts2 = makePts(28, 7, 1.4, 35, H - 28, 30)
  const path1 = pathFrom(pts1, W)
  const path2 = pathFrom(pts2, W)
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} className="mt-3" preserveAspectRatio="none">
      <defs>
        <linearGradient id="apl-grad-acc-ps" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={T.acc} stopOpacity="0.18" />
          <stop offset="100%" stopColor={T.acc} stopOpacity="0" />
        </linearGradient>
        <linearGradient id="apl-grad-blue-ps" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={T.blue} stopOpacity="0.14" />
          <stop offset="100%" stopColor={T.blue} stopOpacity="0" />
        </linearGradient>
      </defs>
      <line x1="0" x2={W} y1={H * 0.25} y2={H * 0.25} stroke="rgba(10,10,10,0.05)" strokeDasharray="2 4" />
      <line x1="0" x2={W} y1={H * 0.65} y2={H * 0.65} stroke="rgba(10,10,10,0.05)" strokeDasharray="2 4" />
      <path d={`${path2} L ${W} ${H} L 0 ${H} Z`} fill="url(#apl-grad-blue-ps)" />
      <path d={`${path1} L ${W} ${H} L 0 ${H} Z`} fill="url(#apl-grad-acc-ps)" />
      <path d={path2} stroke={T.blue} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <path d={path1} stroke={T.acc}  strokeWidth="1.75" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function makePts(n: number, seed: number, vol: number, base: number, max: number, range: number) {
  let s = seed
  const out: number[] = []
  let v = base + range / 2
  for (let i = 0; i < n; i++) {
    s = (s * 9301 + 49297) % 233280
    const r = (s / 233280 - 0.5) * vol * range * 0.4
    v = Math.max(base, Math.min(max, v + r + Math.sin(i / 4) * range * 0.08))
    out.push(v)
  }
  return out
}

function pathFrom(values: number[], width: number) {
  return values
    .map((v, i) => `${i === 0 ? "M" : "L"}${((i / (values.length - 1)) * width).toFixed(2)},${v.toFixed(2)}`)
    .join(" ")
}
